import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, CssBaseline, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useAppDispatch } from '@/store';
import { setIsMobile } from '@/store/slices/uiSlice';
import theme from '@/theme';
import AuthGuard from '../Auth/AuthGuard';
import AppHeader from './AppHeader';
import AppSidebar from './AppSidebar';
import AppFooter from './AppFooter';
import SkipLink from '../UI/SkipLink';
import NotificationContainer from '../UI/NotificationContainer';

const RootLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobileQuery = useMediaQuery(theme.breakpoints.down('md'));
  
  useEffect(() => {
    dispatch(setIsMobile(isMobileQuery));
  }, [dispatch, isMobileQuery]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SkipLink />
      <AuthGuard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          {/* Header */}
          <AppHeader />
          
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Sidebar */}
            <AppSidebar />
            
            {/* Main Content */}
            <Box
              component="main"
              id="main-content"
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                backgroundColor: theme.palette.background.default,
                minHeight: 'calc(100vh - 64px)', // Account for header height
                marginTop: '64px', // Add margin to avoid header overlap
                // The only page padding: pages and PageTitle add none of their own, so the
                // gap from the header bar to the title is the same everywhere.
                p: { xs: 2, md: 3 },
              }}
            >
              <Outlet />
            </Box>
          </Box>
          
          {/* Footer */}
          <AppFooter />
          
          {/* Notifications */}
          <NotificationContainer />
        </Box>
      </AuthGuard>
    </ThemeProvider>
  );
};

export default RootLayout;